import { PaginationResult } from '@/models/pagination.model';
import {
  BaseService,
  Status,
  DataResponse,
  MessageResponse,
  MessageData,
} from './base.service';
import { Initiative } from '@/models/initiative.model';
import { FilterData } from './users.service';

class InitiativesService extends BaseService {
  private url: string = '/api/initiative';

  constructor() {
    super();
  }

  public async get(id: number): Promise<DataResponse<Initiative>> {
    const res = await this.$http.get<Initiative>(`${this.url}/${id}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }
  public async getByUUID(uuid: string): Promise<DataResponse<Initiative>> {
    const res = await this.$http.get<Initiative>(`${this.url}/uuid/${uuid}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getList(): Promise<DataResponse<Initiative[]>> {
    const res = await this.$http.get<Initiative[]>(`${this.url}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getListPaginated(
    filters: FilterData,
  ): Promise<DataResponse<PaginationResult<Initiative[]>>> {
    let query = `?page=${filters.page}&count=${filters.count}`;

    if (filters.search) {
      query = `${query}&search=${filters.search}`;
    }

    const res = await this.$http.get<PaginationResult<Initiative[]>>(
      `${this.url}/list/paginated${query}`,
    );
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getListForContentUser(): Promise<DataResponse<Initiative[]>> {
    const res = await this.$http.get<Initiative[]>(`${this.url}/user/list`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async create(
    item: Partial<Initiative>,
  ): Promise<DataResponse<Initiative>> {
    const res = await this.$http.post<Initiative>(this.url, item);

    return {
      status: [Status.Ok, Status.Created].includes(res.status),
      data: res.data,
      message:
        res.status !== Status.Ok && res.message ? res.message : undefined,
    };
  }

  public async edit(item: Initiative): Promise<DataResponse<Initiative>> {
    const res = await this.$http.put<Initiative>(
      `${this.url}/${item.id}`,
      item,
    );

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async editByUUID(
    uuid: string,
    item: Initiative,
  ): Promise<DataResponse<Initiative>> {
    const res = await this.$http.put<Initiative>(
      `${this.url}/uuid/${uuid}`,
      item,
      undefined,
      { uuid },
    );

    return {
      status: res.status === Status.Ok,
      data: res.data,
      message:
        res.status !== Status.Ok && res.message ? res.message : undefined,
    };
  }

  public async editStatusByUUID(
    uuid: string,
    item: Initiative,
  ): Promise<DataResponse<Initiative>> {
    const res = await this.$http.put<Initiative>(
      `${this.url}/uuid/${uuid}/status`,
      item,
      undefined,
      { uuid },
    );

    return {
      status: res.status === Status.Ok,
      data: res.data,
      message:
        res.status !== Status.Ok && res.message ? res.message : undefined,
    };
  }

  public async search(
    search: string,
    page: number = 0,
    categoryId?: number,
    locationId?: number,
  ): Promise<PaginationResult<Initiative>> {
    const res = await this.$http.get<PaginationResult<Initiative>>(
      `${this.url}/list/paginated?search=${search}${
        categoryId ? `&categories=${categoryId}` : ''
      }${locationId ? `&locations=${locationId}` : ''}&page=${page}&count=10`,
    );

    return res.data;
  }

  public async delete(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(`${this.url}/${id}`);

    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }
}

export default new InitiativesService();
